/**
 * The one viewer every page shows a volume through — DWI, Perfusion and
 * Output alike.
 *
 * Default mode is the plain 2D reader (`SliceCanvas`): it opens instantly,
 * needs no GPU context, and is the shape `GUI_launcher.py` — the desktop
 * reference this redesign is simplifying toward — uses for the same job. A
 * toggle switches to NiiVue for the cases that actually want a 3D engine
 * (true multiplanar reconstruction, volume rendering); the caller supplies
 * that mode's content via `renderNiivue`, because the NiiVue wiring differs
 * by context (the full multi-pane grid in DWI/PWI, a single pane in Output)
 * while the base/overlay picker and the mode toggle are identical everywhere.
 *
 * Base and overlay selection live in the caller (DWI/PWI keep it in
 * `viewerStore` so the ribbon and the rest of the case tooling agree with it;
 * Output keeps it local to whatever folder is open) — switching modes here
 * never resets that selection, it only changes how the same two ids are drawn.
 * URL resolution lives in the caller too (`useResolvedVolumeUrls`), since the
 * NiiVue render prop needs the same resolved URLs this component draws.
 */

import type { ReactNode } from "react";
import { Scan, Box } from "lucide-react";

import { cn } from "@/lib/utils";
import { BaseOverlayPicker, type PickerFile } from "./BaseOverlayPicker";
import { SliceCanvas, type SliceOverlaySpec } from "./SliceCanvas";

export type ViewerMode = "slices" | "niivue";

export interface SharedVolumeFile extends PickerFile {
  /** Resolved lazily — only the base and the checked overlays are fetched. */
  resolve: () => Promise<string | null>;
}

export function SharedVolumeViewer({
  files,
  baseId,
  overlayIds,
  onBaseChange,
  onOverlayToggle,
  baseUrl,
  overlays,
  mode,
  onModeChange,
  renderNiivue,
  className,
}: {
  files: SharedVolumeFile[];
  baseId: string | null;
  overlayIds: string[];
  onBaseChange: (id: string) => void;
  onOverlayToggle: (id: string) => void;
  baseUrl: string | null;
  overlays: SliceOverlaySpec[];
  mode: ViewerMode;
  onModeChange: (mode: ViewerMode) => void;
  renderNiivue: () => ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      <div className="flex shrink-0 items-stretch">
        <div className="min-w-0 flex-1">
          <BaseOverlayPicker
            files={files}
            baseId={baseId}
            overlayIds={overlayIds}
            onBaseChange={onBaseChange}
            onOverlayToggle={onOverlayToggle}
          />
        </div>
        <div className="flex shrink-0 items-center gap-0.5 border-b border-l border-ads-border bg-ads-surface px-2">
          <ModeButton active={mode === "slices"} onClick={() => onModeChange("slices")} icon={Scan} label="Slices" />
          <ModeButton active={mode === "niivue"} onClick={() => onModeChange("niivue")} icon={Box} label="NiiVue" />
        </div>
      </div>
      <div className="min-h-0 flex-1">
        {mode === "slices" ? <SliceCanvas baseUrl={baseUrl} overlays={overlays} /> : renderNiivue()}
      </div>
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Scan;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${label} view`}
      className={cn(
        "flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition",
        active ? "bg-ads-accent-soft text-ads-accent" : "text-neutral-500 hover:bg-ads-hover",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
